// The inline Assist session card's data (29 Oct 2026): the live state of the Assist sessions that
// belong to the OPEN chat, read from the owner-only Assist watch service on this same origin.
//
//   GET  /api/assist-watch/state                 -> { sessions:[...], allStopped, now }      (cheap)
//   POST /api/assist-watch/thumbs {want:[{id,etag}]} -> { items:{ id: {etag,jpeg} | {same:true} | {none} } }
//
// The rules (critic must-fix list, wf6_critique.json, adapted to the live-state design):
// - One module-level store for every card. It polls /state every 10 s, and ONLY while a chat view
//   holds it open (open()), the tab is visible, and the kill switch said on (the component does not
//   call open() otherwise). One request in flight, ever. Errors back off 10 / 20 / 40 / 60 s; a 429
//   waits its retry-after; a 401 gets at most ONE force-refreshed token per rejected token; a 503
//   (owner API off) is quiet: no card, slow re-check.
// - A thumbnail can make the Assist box take a screenshot, so thumbs are asked for only when the
//   session's last action moved, no picture is held, or 60 s passed (the server's own changed/maxAge rule).
// - The store keeps ONLY the sessions whose `chat` is the open chat's id. Other sessions (other
//   clients' labels, Julian's own laptop given to another chat) are dropped at the door, before
//   anything is kept, so they can never reach the DOM.
// - PRIVACY: a picture exists only while the session is LIVE. Any other state (idle, stopped, unknown,
//   a failed read for longer than 30 s, a 401, a hidden tab coming back after 30 s) drops the frame and
//   releases its object URL at once. Object URLs are reference counted: revoked when nobody holds them.
// - Nothing here can throw into React: every entry point is wrapped, the snapshot is plain data.
// - Ages are coarse ("just now", "under a minute", "2 min ago"): polls are 10 s apart, so no
//   per-second precision is claimed and no timer runs between polls.
// - The Firebase ID token goes only in the Authorization header of a plain same-origin fetch to the
//   ABSOLUTE /api/assist-watch/ URL (authFetch would rewrite /api/ to /api/build/: the wrong backend).
import { getCurrentIdToken } from "../auth-context";

export const ASSIST_BASE = "/assist/";
export const POLL_MS = 10_000;
export const BACKOFF_MS = [10_000, 20_000, 40_000, 60_000] as const;
/** 401 after the one retry, or 503 (owner API off): look again this much later. */
export const QUIET_MS = 60_000;
/** A thumbnail is refreshed at least this often while live, even on a still screen. */
export const THUMB_MAX_AGE_MS = 60_000;
/** No good answer for this long: the cards say "Preview unavailable" and drop their pictures. */
export const STALE_MS = 30_000;
/** A stopped session keeps its card this long after the stop (when the stop time is known). */
export const STOPPED_SHOW_SEC = 15 * 60;
/** An idle session (nothing connected) keeps its card this long after its last sign of life. */
export const IDLE_SHOW_SEC = 15 * 60;
/** A CONNECTED session nobody has touched this long (the box's own reaper calls a slot unused after the same 30 min) no longer
 *  keeps a card under the chat: an agent that finished and forgot to disconnect must not leave one there for good. Its next
 *  action brings the card back; a pause or a request for him is never hidden. */
export const LIVE_QUIET_SEC = 30 * 60;
const REQUEST_TIMEOUT_MS = 8_000;
/** One whole poll (token + state + thumbs) may take this long at most. */
const WATCHDOG_MS = 25_000;
const MAX_THUMBS = 7;
const MAX_JPEG_B64 = 4 * 1024 * 1024;

const SID_RE = /^(default|s[1-6])$/;
const CHAT_RE = /^[a-z0-9][a-z0-9_-]{0,127}$/i;
const ETAG_RE = /^[0-9a-f]{1,32}$/;

// ------------------------------------------------------------------------- text (the Assist page's own rules)
// Control, format (bidi, zero-width), line/paragraph separators and the Hangul fillers go; ZWNJ/ZWJ stay
// (they join real scripts and emoji). Whitespace collapses. Same as page/index.html clean().
const UNSAFE = /(?![‌‍])[\p{Cc}\p{Cf}\p{Zl}\p{Zp}ᅟᅠㅤﾠ]/gu;
export function clean(s: unknown): string {
  return String(s == null ? "" : s).replace(UNSAFE, "").replace(/\s+/g, " ").trim();
}
/** At most n characters (code points), with an ellipsis when cut. */
export function cut(s: string, n: number): string {
  const a = Array.from(s);
  return a.length > n ? a.slice(0, n - 1).join("").replace(/\s+$/, "") + "…" : s;
}
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const has = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

// The only words ever shown about what the agent did: the Assist page's whitelist (classifySummary).
// Never typed text, a key's letter, coordinates, a URL, a window title or a file name.
const ACTIONS: Record<string, 1> = { clicked: 1, scrolled: 1, dragged: 1, "held a click": 1, "pressed a key": 1, "pressed an arrow key": 1, "used a shortcut": 1 };
const LIFECYCLE: Record<string, 1> = { connected: 1, disconnected: 1, stopped: 1, "opened the session": 1, resumed: 1 };
const OWNER: Record<string, 1> = { "you paused": 1, "asked for you": 1, "you resumed": 1, "you resumed with a message": 1, "you clicked": 1,
  "you right-clicked": 1, "you dragged": 1, "you scrolled": 1, "you pressed a key": 1, "you typed": 1, "you opened your laptop": 1 };
const KEYNAMES: Record<string, string> = { enter: "Enter", tab: "Tab", esc: "Esc", escape: "Esc", backspace: "Backspace", delete: "Delete",
  up: "Up", down: "Down", left: "Left", right: "Right" };
export function classifySummary(raw: unknown): { kind: "action" | "lifecycle" | "owner" | "unknown"; text: string } {
  const lc = (typeof raw === "string" ? clean(raw) : "").toLowerCase();
  if (has(ACTIONS, lc)) return { kind: "action", text: lc };
  if (has(LIFECYCLE, lc)) return { kind: "lifecycle", text: lc };
  let m = /^typed (\d{1,5}|some) (character|characters)$/.exec(lc);
  if (m) return { kind: "action", text: m[1] === "some" ? "typed" : "typed " + m[1] };
  m = /^pressed (enter|tab|esc|escape|backspace|delete|up|down|left|right)$/.exec(lc);
  if (m) return { kind: "action", text: "pressed " + KEYNAMES[m[1]!] };
  if (has(OWNER, lc)) return { kind: "owner", text: lc };
  return { kind: "unknown", text: "did something" };
}

/** Coarse on purpose (polls are 10 s apart): "just now", "under a minute", "2 min ago", "3 h ago", "2 d ago". */
export function ageWords(sec: number | null): string {
  if (sec === null || !Number.isFinite(sec)) return "";
  const s = Math.max(0, sec);
  if (s < 20) return "just now";
  if (s < 60) return "under a minute";
  if (s < 3600) return Math.floor(s / 60) + " min ago";
  if (s < 86400) return Math.floor(s / 3600) + " h ago";
  return Math.floor(s / 86400) + " d ago";
}

// ------------------------------------------------------------------------- sessions
export interface WatchAction { t: number; summary: string }
export interface WatchSession {
  id: string;
  label: string | null;
  state: "live" | "idle" | "stopped" | "unknown";
  lock: "agent" | "owner" | "all" | null;
  openedAt: number | null;
  lastAction: WatchAction | null;
  recent: WatchAction[];
  paused: { by: "owner" | "agent"; reason: string | null } | null;
  agentWaiting: boolean | null;
  chat: string | null;
}

function action(a: unknown): WatchAction | null {
  if (!a || typeof a !== "object") return null;
  const t = num((a as { t?: unknown }).t);
  if (t === null) return null;
  return { t, summary: clean((a as { summary?: unknown }).summary).slice(0, 120) };
}
function pausedOf(p: unknown): WatchSession["paused"] {
  if (p === true) return { by: "owner", reason: null };
  if (!p || typeof p !== "object" || Array.isArray(p)) return null;
  const r = (p as { reason?: unknown }).reason;
  return { by: (p as { by?: unknown }).by === "agent" ? "agent" : "owner", reason: r == null ? null : cut(clean(r), 120) || null };
}
/** The page's normalizeSession, reduced to what a card needs. A lock or Stop-all means stopped. */
export function normalizeSession(raw: unknown, allStopped: boolean): WatchSession | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || !SID_RE.test(r.id)) return null;
  const lock = r.lock === "agent" || r.lock === "owner" || r.lock === "all" ? r.lock : null;
  let state: WatchSession["state"] = r.state === "live" || r.state === "idle" || r.state === "stopped" ? r.state : "unknown";
  if ((lock !== null || r.stopped === true || allStopped) && state !== "stopped") state = "stopped";
  const recent: WatchAction[] = [];
  if (Array.isArray(r.recent)) for (const it of r.recent) { const a = action(it); if (a) recent.push(a); if (recent.length >= 5) break; }
  return {
    id: r.id,
    label: r.label == null ? null : cut(clean(r.label), 80) || null,
    state,
    lock: state === "stopped" && lock === null && allStopped ? "all" : lock,
    openedAt: num(r.openedAt),
    lastAction: action(r.lastAction),
    recent,
    paused: pausedOf(r.paused),
    agentWaiting: typeof r.agentWaiting === "boolean" ? r.agentWaiting : null,
    chat: typeof r.chat === "string" && CHAT_RE.test(r.chat) ? r.chat : null,
  };
}
const slotRank = (id: string) => (id === "default" ? 0 : Number(id.slice(1)));

/** When the session was stopped (the newest "stopped" line), in server seconds, or null. */
export function stoppedAt(s: WatchSession): number | null {
  let best: number | null = null;
  for (const a of [s.lastAction, ...s.recent]) if (a && classifySummary(a.summary).text === "stopped" && (best === null || a.t > best)) best = a.t;
  return best;
}
/** The newest thing known about a session (an action, or when it was opened), in server seconds, or null. */
function lastTouch(s: WatchSession): number | null {
  let best: number | null = s.openedAt;
  for (const a of [s.lastAction, ...s.recent]) if (a && (best === null || a.t > best)) best = a.t;
  return best;
}
/**
 * A card is shown for a session that matters now. Live ones while something touched them in the last 30 minutes, paused ones always. A STOP by Julian (or Stop all) always:
 * the agent in this chat is frozen until he presses Resume, and the card is what points him there. An agent's own stop
 * and an idle session (nothing connected) only for a while after their last sign of life: an idle "Emergency laptop"
 * must not sit under a chat for days.
 */
export function isRelevant(s: WatchSession, nowSec: number | null): boolean {
  if (s.state === "live") {
    if (s.paused) return true;                             // his takeover or a request for him: never hidden
    const at = lastTouch(s);
    return at === null || nowSec === null || nowSec - at <= LIVE_QUIET_SEC;
  }
  if (s.state === "stopped") {
    if (s.lock === "owner" || s.lock === "all") return true;
    const at = stoppedAt(s);
    return at === null || nowSec === null || nowSec - at <= STOPPED_SHOW_SEC;
  }
  if (s.paused) return true;                               // a handover or a pause on a session that just dropped: he is needed
  if (s.state === "idle") {
    const at = lastTouch(s);
    return at !== null && nowSec !== null && nowSec - at <= IDLE_SHOW_SEC;
  }
  return true;                                             // unknown: say so
}

export function sessionTitle(s: Pick<WatchSession, "id" | "label">): string {
  const n = clean(s.label);
  return cut(n || (s.id === "default" ? "Emergency laptop" : "Client computer"), 60);
}
export const sessionHref = (id: string) => (SID_RE.test(id) ? `${ASSIST_BASE}#/s/${id}` : ASSIST_BASE);

export type CardKey = "live" | "paused" | "needs" | "idle" | "stopped" | "unknown" | "unavailable" | "signedout";
export interface CardView { key: CardKey; line: string; dot: boolean; picture: boolean; working: boolean }
/** An agent whose own last action is younger than this is "working": the card flashes (a live ping dot and an edge). */
export const WORKING_SEC = 60;
/**
 * What one card says, as plain data (pure; unit tested). `picture` is true ONLY for a live session
 * (a pause sits on a live session: the agent waits, the screen is still live, as on the Assist page).
 */
export function cardView(s: WatchSession, o: { nowSec: number | null; stale?: boolean; signedOut?: boolean }): CardView {
  if (o.signedOut) return { key: "signedout", line: "Sign in again to see this", dot: false, picture: false, working: false };
  const live = s.state === "live";
  // A read that keeps failing: the last words about a request for him (Needs you, Paused, Stopped) are still the best
  // there is, only the picture goes. Live and idle say nothing more than "Preview unavailable".
  if (o.stale) {
    const v = cardView(s, { nowSec: o.nowSec });
    if (v.key === "needs" || v.key === "paused" || v.key === "stopped") return { ...v, line: v.line + " · not updating", dot: false, picture: false, working: false };
    return { key: "unavailable", line: "Preview unavailable", dot: false, picture: false, working: false };
  }
  if (s.paused && (live || s.state === "idle")) {
    if (s.paused.by === "agent") return { key: "needs", line: "Needs you · " + cut(s.paused.reason || "Finish this step on the screen", 80), dot: false, picture: live, working: false };
    return { key: "paused", line: s.agentWaiting === true ? "Paused · the agent is waiting" : "Paused", dot: false, picture: live, working: false };
  }
  if (live) {
    const a = s.lastAction;
    if (!a) return { key: "live", line: "Connected · no actions yet", dot: true, picture: true, working: false };
    const c = classifySummary(a.summary), sec = o.nowSec === null ? null : o.nowSec - a.t, age = ageWords(sec);
    // Working = the AGENT acted a moment ago (not Julian's own input, not a connect/disconnect line): the card flashes
    const working = c.kind === "action" && sec !== null && sec < WORKING_SEC;
    const parts = [working ? "Working" : "Connected"];
    if (c.kind !== "lifecycle") parts.push(c.text);
    if (age) parts.push(age);
    return { key: "live", line: parts.join(" · "), dot: true, picture: true, working };
  }
  if (s.state === "idle") return { key: "idle", line: "Idle · not connected", dot: false, picture: false, working: false };
  if (s.state === "stopped") {
    const who = s.lock === "owner" || s.lock === "all" ? "Stopped by you" : s.lock === "agent" ? "Stopped by the agent" : "Stopped";
    const at = stoppedAt(s), age = ageWords(at === null || o.nowSec === null ? null : o.nowSec - at);
    return { key: "stopped", line: age ? who + " · " + age : who, dot: false, picture: false, working: false };
  }
  return { key: "unknown", line: "Status unknown", dot: false, picture: false, working: false };
}

// ------------------------------------------------------------------------- object URLs (reference counted)
const urlRefs = new Map<string, number>();
export function retainUrl(url: string | null | undefined): void {
  if (!url) return;
  urlRefs.set(url, (urlRefs.get(url) ?? 0) + 1);
}
export function releaseUrl(url: string | null | undefined): void {
  if (!url) return;
  const n = (urlRefs.get(url) ?? 0) - 1;
  if (n > 0) { urlRefs.set(url, n); return; }
  urlRefs.delete(url);
  try { URL.revokeObjectURL(url); } catch { /* already gone */ }
}
/** Test hook: how many object URLs are still held. */
export const heldUrlCount = () => urlRefs.size;

function jpegUrl(b64: string): string | null {
  if (typeof b64 !== "string" || !b64 || b64.length > MAX_JPEG_B64 || !/^[A-Za-z0-9+/]+={0,2}$/.test(b64)) return null;
  try {
    const bin = atob(b64), bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: "image/jpeg" }));
    retainUrl(url);
    return url;
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------------- the store
export interface CardModel extends CardView {
  /** React key: a reopened slot (another openedAt) is another card. */
  key2: string;
  id: string;
  title: string;
  href: string;
  /** The object URL of the latest live frame, or null. */
  src: string | null;
  /** Live, but no frame yet: the picture area waits (a flat pulse). */
  waiting: boolean;
}
export interface Snapshot { chatId: string | null; cards: CardModel[] }
const EMPTY: Snapshot = Object.freeze({ chatId: null, cards: [] }) as Snapshot;

type Frame = { url: string; etag: string; actionT: number | null; openedAt: number | null };
type Answer =
  | { kind: "ok"; body: unknown }
  | { kind: "unauth" }
  | { kind: "disabled" }
  | { kind: "limited"; retryMs: number }
  | { kind: "error" };

/** The seams a test replaces (fetch, the token, the clock, the document). */
export interface WatchDeps {
  fetch: typeof fetch;
  token: (force: boolean) => Promise<string | null>;
  now: () => number;
  origin: () => string;
  visible: () => boolean;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (t: unknown) => void;
}
const realDeps = (): WatchDeps => ({
  fetch: (...a) => fetch(...a),
  token: (force) => getCurrentIdToken(force),
  now: () => Date.now(),
  origin: () => window.location.origin,
  visible: () => typeof document === "undefined" || document.visibilityState === "visible",
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (t) => window.clearTimeout(t as number),
});

export function createAssistWatch(deps: WatchDeps = realDeps()) {
  const listeners = new Set<() => void>();
  let snapshot: Snapshot = EMPTY;
  let snapKey = "";
  let holders = 0;
  let chatId: string | null = null;
  let generation = 0;
  let sessions: WatchSession[] = [];
  const frames = new Map<string, Frame>();
  const thumbAt = new Map<string, number>();
  /** Sessions the server said "no picture" for (no_display): the action time then, so a new action asks again. */
  const noPic = new Map<string, number>();
  let serverNow: number | null = null;
  let arrivedAt = 0;
  let lastOkAt = 0;
  let signedOut = false;
  let failures = 0;
  let blockedUntil = 0;
  let nextAt = 0;
  let timer: unknown = null;
  let inFlight = false;
  /** Tokens already answered 401 and force-refreshed once: never again (no hammering the sign-in). */
  const retried: string[] = [];
  let listening = false;

  const safe = <T,>(fn: () => T, fallback: T): T => { try { return fn(); } catch { return fallback; } };

  function dropFrame(id: string) {
    const f = frames.get(id);
    if (f) { frames.delete(id); releaseUrl(f.url); }
    thumbAt.delete(id); noPic.delete(id);
  }
  function dropAllFrames() { for (const id of [...frames.keys()]) dropFrame(id); }

  function build(): Snapshot {
    if (!chatId || !sessions.length) return { chatId, cards: [] };
    const t = deps.now();
    const nowSec = serverNow === null ? null : serverNow + Math.max(0, t - arrivedAt) / 1000;
    // Stale = failing reads for longer than STALE_MS (a tab back from the background is not "failing").
    const stale = failures > 0 && lastOkAt > 0 && t - lastOkAt > STALE_MS;
    const cards: CardModel[] = [];
    for (const s of sessions) {
      if (!isRelevant(s, nowSec)) continue;
      const v = cardView(s, { nowSec, stale, signedOut });
      const f = v.picture ? frames.get(s.id) : undefined;
      cards.push({ ...v, key2: `${s.id}:${s.openedAt ?? ""}`, id: s.id, title: sessionTitle(s), href: sessionHref(s.id),
        src: f ? f.url : null, waiting: v.picture && !f && !noPic.has(s.id) });
    }
    return { chatId, cards };
  }
  function emit() {
    const next = safe(build, { chatId, cards: [] });
    const key = safe(() => JSON.stringify(next), String(Math.random()));
    if (key === snapKey) return;
    snapKey = key;
    snapshot = next;
    for (const l of [...listeners]) safe(() => l(), undefined);
  }

  function clearTimer() { if (timer !== null) { deps.clearTimeout(timer); timer = null; } }
  const running = () => holders > 0 && chatId !== null && deps.visible();
  function schedule(delay: number) {
    clearTimer();
    nextAt = deps.now() + Math.max(0, delay);
    if (!running() || inFlight) return;
    timer = deps.setTimeout(() => { timer = null; void tick(); }, Math.max(0, delay));
  }

  async function call(method: "GET" | "POST", route: "state" | "thumbs", body?: unknown): Promise<Answer> {
    // The timer covers the BODY too (a stalled body after good headers used to leave inFlight true for ever).
    const send = async (token: string): Promise<{ res: Response; data: unknown }> => {
      const abort = new AbortController();
      const t = deps.setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);
      try {
        const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
        if (body !== undefined) headers["Content-Type"] = "application/json";
        const res = await deps.fetch(`${deps.origin()}/api/assist-watch/${route}`, {
          method, headers, cache: "no-store", signal: abort.signal, ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        });
        return { res, data: res.ok ? await res.json() : undefined };
      } finally { deps.clearTimeout(t); }
    };
    try {
      let token = await deps.token(false);
      if (!token) return { kind: "unauth" };
      let { res, data } = await send(token);
      if (res.status === 401 && !retried.includes(token)) {
        retried.push(token); if (retried.length > 4) retried.shift();
        try { await res.body?.cancel(); } catch { /* best effort */ }
        token = await deps.token(true);
        if (!token) return { kind: "unauth" };
        ({ res, data } = await send(token));
      }
      if (res.status === 401 || res.status === 403) { if (!retried.includes(token)) { retried.push(token); if (retried.length > 4) retried.shift(); } return { kind: "unauth" }; }
      if (res.status === 503) return { kind: "disabled" };
      if (res.status === 429) {
        const ra = Number(res.headers.get("retry-after"));
        return { kind: "limited", retryMs: Number.isFinite(ra) && ra > 0 ? Math.min(ra, 600) * 1000 : QUIET_MS };
      }
      if (!res.ok) return { kind: "error" };
      return { kind: "ok", body: data };
    } catch {
      return { kind: "error" };
    }
  }

  function backoff(): number {
    failures++;
    return BACKOFF_MS[Math.min(failures - 1, BACKOFF_MS.length - 1)]!;
  }

  function applyState(body: unknown): boolean {
    if (!body || typeof body !== "object" || !Array.isArray((body as { sessions?: unknown }).sessions)) return false;
    const b = body as { sessions: unknown[]; allStopped?: unknown; now?: unknown };
    const all = b.allStopped === true;
    const mine: WatchSession[] = [];
    const seen = new Set<string>();
    for (const raw of b.sessions.slice(0, 12)) {
      // Matched at the door: a session of another chat is never kept (not its label, not its id).
      if (!raw || typeof raw !== "object" || (raw as { chat?: unknown }).chat !== chatId) continue;
      const s = normalizeSession(raw, all);
      if (s && s.chat === chatId && !seen.has(s.id)) { seen.add(s.id); mine.push(s); }
    }
    mine.sort((a, c) => slotRank(a.id) - slotRank(c.id));
    sessions = mine;
    serverNow = num(b.now);
    arrivedAt = deps.now();
    // PRIVACY: a frame lives only while its session is live (and is the same opening of that slot).
    for (const [id, f] of [...frames]) {
      const s = mine.find((x) => x.id === id);
      if (!s || s.state !== "live" || s.openedAt !== f.openedAt) dropFrame(id);
    }
    return true;
  }

  function thumbWants(): { id: string; etag?: string }[] {
    const t = deps.now(), out: { id: string; etag?: string }[] = [];
    const nowSec = serverNow;
    for (const s of sessions) {
      if (s.state !== "live" || !isRelevant(s, nowSec)) continue;
      const f = frames.get(s.id);
      const moved = !!f && s.lastAction !== null && (f.actionT === null || s.lastAction.t > f.actionT);
      const old = t - (thumbAt.get(s.id) ?? 0) >= THUMB_MAX_AGE_MS;
      const np = noPic.get(s.id);
      const quiet = np !== undefined && (s.lastAction?.t ?? 0) <= np;     // told "no picture" and nothing has happened since
      if ((!f && !quiet) || moved || old) out.push(f && ETAG_RE.test(f.etag) ? { id: s.id, etag: f.etag } : { id: s.id });
      if (out.length >= MAX_THUMBS) break;
    }
    return out;
  }

  function applyThumbs(body: unknown) {
    const items = body && typeof body === "object" ? (body as { items?: unknown }).items : null;
    if (!items || typeof items !== "object") return;
    for (const s of sessions) {
      if (!has(items, s.id)) continue;
      const it = (items as Record<string, unknown>)[s.id];
      if (!it || typeof it !== "object") continue;
      const r = it as { etag?: unknown; jpeg?: unknown; same?: unknown; none?: unknown };
      if (s.state !== "live") { dropFrame(s.id); continue; }
      const t = deps.now();
      if (typeof r.jpeg === "string" && typeof r.etag === "string") {
        const url = jpegUrl(r.jpeg);
        if (!url) continue;
        dropFrame(s.id);
        frames.set(s.id, { url, etag: r.etag, actionT: s.lastAction?.t ?? null, openedAt: s.openedAt });
        thumbAt.set(s.id, t);
      } else if (r.same === true) {
        const f = frames.get(s.id);
        if (f) f.actionT = s.lastAction?.t ?? f.actionT;
        thumbAt.set(s.id, t);
      } else if (r.none === "idle" || r.none === "no_display") {
        // The server's own privacy gate says "no picture": none is shown, and it is asked again only when something
        // happens or after the 60 s rule (not every poll, and no endless loading pulse).
        dropFrame(s.id);
        thumbAt.set(s.id, t); noPic.set(s.id, s.lastAction?.t ?? 0);
      } else if (r.none === "busy") {
        noPic.delete(s.id);       // transient: keep what there is, ask again next time
      }
    }
  }

  async function cycle(gen: number): Promise<number> {
    const r = await call("GET", "state");
    if (gen !== generation) return 0;
    if (r.kind === "ok" && applyState(r.body)) {
      failures = 0; signedOut = false; lastOkAt = deps.now();
      const want = thumbWants();
      emit();
      if (want.length) {
        const t = await call("POST", "thumbs", { want });
        if (gen !== generation) return 0;
        if (t.kind === "ok") applyThumbs(t.body);
        else if (t.kind === "limited") { blockedUntil = deps.now() + t.retryMs; }
        else if (t.kind === "unauth") { signedOut = true; dropAllFrames(); }
        emit();
        if (t.kind === "limited") return Math.max(POLL_MS, t.retryMs);
        if (t.kind === "unauth") return QUIET_MS;
      }
      return POLL_MS;
    }
    if (r.kind === "unauth") {
      signedOut = true; dropAllFrames(); emit();
      return QUIET_MS;
    }
    if (r.kind === "disabled") {
      // The owner API is off on the box: quiet, no card at all.
      sessions = []; dropAllFrames(); emit();
      return QUIET_MS;
    }
    if (r.kind === "limited") {
      const wait = Math.max(r.retryMs, backoff());
      blockedUntil = deps.now() + wait;
      if (deps.now() - lastOkAt > STALE_MS) dropAllFrames();
      emit();
      return wait;
    }
    // Network error, 5xx, a bad body: back off; after 30 s without a good answer no picture is shown.
    const wait = backoff();
    blockedUntil = deps.now() + wait;
    if (deps.now() - lastOkAt > STALE_MS) dropAllFrames();
    emit();
    return wait;
  }

  async function tick() {
    if (inFlight || !running()) return;
    inFlight = true;
    const gen = generation;
    let next: number;
    // A watchdog: whatever stalls (the token call, a body), the store is never stuck in flight for good.
    let dog: unknown = null;
    const watchdog = new Promise<number>((resolve) => { dog = deps.setTimeout(() => resolve(-1), WATCHDOG_MS); });
    try {
      next = await Promise.race([cycle(gen), watchdog]);
      if (next === -1) { generation++; next = backoff(); blockedUntil = deps.now() + next; }   // the stuck cycle's late answer is ignored from here on
    } catch { next = backoff(); } finally { deps.clearTimeout(dog); inFlight = false; }
    if (gen !== generation) next = Math.max(0, blockedUntil - deps.now());
    schedule(next);
  }

  function onVisibility() {
    safe(() => {
      if (!deps.visible()) { clearTimer(); return; }
      // Back at the front after a while: an old picture is never passed off as now.
      if (lastOkAt && deps.now() - lastOkAt > STALE_MS) { dropAllFrames(); emit(); }
      schedule(Math.max(0, blockedUntil - deps.now(), nextAt - deps.now()));
    }, undefined);
  }
  function listen(on: boolean) {
    if (on === listening || typeof document === "undefined") return;
    listening = on;
    if (on) { document.addEventListener("visibilitychange", onVisibility); window.addEventListener("pageshow", onVisibility); }
    else { document.removeEventListener("visibilitychange", onVisibility); window.removeEventListener("pageshow", onVisibility); }
  }

  function reset(next: string | null) {
    generation++;
    chatId = next;
    sessions = [];
    dropAllFrames();
    serverNow = null; lastOkAt = 0; signedOut = false;
    emit();
  }

  return {
    subscribe(fn: () => void): () => void {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
    getSnapshot: (): Snapshot => snapshot,
    /** A chat view shows the cards of `id`. Returns the release. Never throws. */
    open(id: string): () => void {
      return safe(() => {
        if (!CHAT_RE.test(id)) return () => {};
        holders++;
        listen(true);
        if (chatId !== id) { reset(id); schedule(Math.max(0, blockedUntil - deps.now())); }
        else if (timer === null && !inFlight) schedule(Math.max(0, blockedUntil - deps.now(), nextAt - deps.now()));
        let released = false;
        return () => {
          if (released) return;
          released = true;
          safe(() => {
            holders = Math.max(0, holders - 1);
            if (holders === 0) { clearTimer(); listen(false); reset(null); }
          }, undefined);
        };
      }, () => {});
    },
    /** Test hooks. */
    _debug: () => ({ holders, chatId, inFlight, frames: frames.size, failures, blockedUntil, timer: timer !== null }),
  };
}

export type AssistWatch = ReturnType<typeof createAssistWatch>;
let shared: AssistWatch | null = null;
/** The one store every card shares. */
export function assistWatch(): AssistWatch {
  if (!shared) shared = createAssistWatch();
  return shared;
}
